import {
  NotificationAction,
  NotificationActionPayloads,
  useNotificationAction,
} from "@config/local-notifications/notification-actions";
import { Button, Stack } from "@mui/material";
import { UserNotification } from "@prisma/client";
import { JsonValue } from "@prisma/client/runtime/library";
import React, { useMemo } from "react";

/**
 * 📦 Props for rendering a custom button.
 */
interface RenderButtonProps {
  /** ⚡ Function to call on button click */
  onClick: VoidFunction;

  /** 📝 Button text */
  children: string;

  /** 🆔 Optional identifier, passed for custom renderers */
  key: string;
}

/**
 * 📬 Props for the NotificationActions component.
 */
interface NotificationActionsProps {
  /** 🔔 A Notification entity containing action definitions */
  notification: UserNotification;

  /** Called when a navigate action is triggered (also marks as read upstream). */
  onNavigate?: VoidFunction;

  /**
   * 🎨 Optional custom renderer for each action button.
   * If not provided, a native `<button>` will be used.
   */
  renderButton?: (props: RenderButtonProps) => React.ReactNode;
}

/**
 * 🚀 Renders a list of action buttons for a given notification.
 *
 * Each button is associated with a `NotificationAction` and calls `handleNotificationAction`
 * when clicked. Supports custom rendering via the `renderButton` prop.
 */
const NotificationActions: React.FC<NotificationActionsProps> = (props) => {
  const { notification, renderButton: _renderButton, onNavigate } = props;

  const handleNotificationAction = useNotificationAction();

  /**
   * 🛠️ Memoized button rendering function.
   * Falls back to modern Material-UI Button if no custom renderer is provided.
   */
  const renderButton = useMemo<
    (props: RenderButtonProps) => React.ReactNode
  >(() => {
    if (_renderButton) return _renderButton;

    return (props) => (
      <Button
        variant="outlined"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          props.onClick();
        }}
        sx={{
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 400,
          fontSize: "0.75rem",
          padding: "0 8px",
          borderWidth: "1px",
          minWidth: "auto",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        {props.children}
      </Button>
    );
  }, [_renderButton]);

  if (!(notification.actions as (JsonValue & NotificationActionPayloads)[])?.length) return null;

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        marginTop: 1,
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      {(notification.actions as (JsonValue & NotificationActionPayloads)[]).map((action, index) => (
        <React.Fragment key={action.id + "-" + index}>
          {renderButton({
            children: action.btnText,
            onClick: () => {
              if (action.id === NotificationAction.NAVIGATE && onNavigate) {
                onNavigate();
                return;
              }
              handleNotificationAction(action);
            },
            key: action.id + "-" + index,
          })}
        </React.Fragment>
      ))}
    </Stack>
  );
};

export default NotificationActions;
